import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { mockSalesOrders, mockSODeliveryEvents, type MockSalesOrder } from '@/mocks/billing';
import { mockParties } from '@/mocks/masters';
import { formatINR } from '@/utils/format';
import { salesOrderService } from '@/services/salesOrderService';

const STATUS_STYLES: Record<string, string> = {
  DRAFT: 'bg-slate-100 text-slate-600 border-slate-200',
  CONFIRMED: 'bg-blue-50 text-blue-700 border-blue-200',
  PARTIAL: 'bg-amber-50 text-amber-700 border-amber-200',
  COMPLETED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  CANCELLED: 'bg-red-50 text-red-600 border-red-200',
};

export default function SalesOrderDetailPage() {
  const navigate = useNavigate();
  const soId = window.location.pathname.split('/').pop() || '';

  const [so, setSo] = useState<MockSalesOrder | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isEditNumberOpen, setIsEditNumberOpen] = useState(false);

  useEffect(() => {
    async function loadSO() {
      if (!soId) return;
      setLoading(true);
      try {
        const fetched = await salesOrderService.getSalesOrder(soId);
        setSo(fetched || null);
      } catch (err) {
        console.warn('Failed to load sales order from API:', err);
        setSo(null);
      } finally {
        setLoading(false);
      }
    }
    loadSO();
  }, [soId]);

  if (loading) {
    return (
      <AppLayout>
        <div className="p-10 text-center text-slate-500">Loading sales order detail...</div>
      </AppLayout>
    );
  }

  if (!so) {
    return (
      <AppLayout>
        <div className="p-10 text-center">
          <h1 className="text-xl font-bold text-slate-800">Sales Order Not Found</h1>
          <button onClick={() => navigate('/sales/orders')} className="mt-4 text-indigo-600 hover:underline cursor-pointer">Back to Orders</button>
        </div>
      </AppLayout>
    );
  }

  const events = mockSODeliveryEvents.filter((e) => e.soId === so.id).sort((a, b) => a.date.localeCompare(b.date));
  const party = mockParties.find((p) => p.id === so.customerId);
  const pct = so.totalAmount > 0 ? Math.round((so.deliveredAmount / so.totalAmount) * 100) : 0;

  const handleCopySO = () => {
    navigator.clipboard.writeText(so.soNumber);
  };

  const handleCreateInvoice = () => {
    navigate('/sales/invoices/new', {
      state: {
        fromSalesOrder: {
          soId: so.id,
          soNumber: so.soNumber,
          customerId: so.customerId,
          customerName: so.customerName,
          customerGSTIN: so.customerGSTIN,
          items: (so.items || []).filter((i) => i.pendingQty > 0).map((i) => ({
            itemId: i.itemId,
            itemName: i.itemName,
            hsnCode: i.hsnCode,
            qty: i.pendingQty,
            unit: i.unit,
            rate: i.rate,
            discount: i.discount,
            taxRate: i.taxRate,
          })),
        },
      },
    });
  };

  const handleCreateChallan = () => {
    navigate('/sales/challans', {
      state: {
        fromSalesOrder: {
          soId: so.id,
          soNumber: so.soNumber,
          customerId: so.customerId,
          customerName: so.customerName,
          warehouseId: (so as any).warehouseId,
          items: (so.items || []).filter((i) => (i.pendingQty ?? i.orderedQty ?? 0) > 0).map((i) => ({
            itemId: i.itemId,
            itemCode: i.itemCode,
            itemName: i.itemName,
            hsnCode: i.hsnCode,
            orderedQty: i.orderedQty,
            deliveredQty: i.deliveredQty,
            pendingQty: i.pendingQty ?? i.orderedQty,
            qty: (i.pendingQty && i.pendingQty > 0) ? i.pendingQty : (i.orderedQty || 1),
            unit: i.unit,
            unitId: i.unitId,
            rate: i.rate,
            discount: i.discount,
            taxRate: i.taxRate,
            taxableAmount: i.taxableAmount,
            total: i.total,
          })),
        },
      },
    });
  };

  const getItemStatus = (delivered: number, ordered: number) => {
    if (delivered === 0) return { label: 'Pending', cls: 'bg-slate-100 text-slate-600' };
    if (delivered < ordered) return { label: 'Partial', cls: 'bg-amber-100 text-amber-700' };
    return { label: 'Fulfilled', cls: 'bg-emerald-100 text-emerald-700' };
  };

  const canEdit = so.status === 'DRAFT';
  const itemsList = so.items || [];
  const canCancel = (so.status === 'DRAFT' || so.status === 'CONFIRMED') && itemsList.every((i) => i.deliveredQty === 0);

  const handleCancelOrder = async () => {
    try {
      await salesOrderService.updateStatus(so.id, 'CANCELLED');
      setSo((prev) => (prev ? { ...prev, status: 'CANCELLED' } : null));
    } catch (err) {
      console.error('Error cancelling order:', err);
      const idx = mockSalesOrders.findIndex((x) => x.id === so.id);
      if (idx >= 0) mockSalesOrders[idx] = { ...mockSalesOrders[idx], status: 'CANCELLED' };
      setSo((prev) => (prev ? { ...prev, status: 'CANCELLED' } : null));
    }
  };

  return (
    <AppLayout>
      <div className="p-6 bg-[#f8fafc] min-h-full">
        {/* Back */}
        <button onClick={() => navigate('/sales/orders')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-indigo-600 mb-4 cursor-pointer transition-colors">
          <i className="ri-arrow-left-line" /> Back to Sales Orders
        </button>

        {/* Header */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-lg font-bold text-slate-800 flex items-center gap-1.5">
                  {so.soNumber}
                </h1>
                <button onClick={handleCopySO} className="text-xs text-indigo-500 hover:text-indigo-700 cursor-pointer" title="Copy SO number">
                  <i className="ri-file-copy-line" /> Copy
                </button>
                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_STYLES[so.status] || STATUS_STYLES.DRAFT}`}>
                  {so.status}
                </span>
              </div>
              <p className="text-sm text-slate-600">
                <strong>{so.customerName}</strong>
                {so.customerGSTIN && <span className="ml-2 text-slate-400">GSTIN: {so.customerGSTIN}</span>}
              </p>
              {party && <p className="text-xs text-slate-400 mt-0.5">{party.billingAddress}</p>}
              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-500">
                <span>Created: {so.date}</span>
                {so.expectedDeliveryDate && <span>Expected: {so.expectedDeliveryDate}</span>}
                {so.paymentTerms && <span>Payment: {so.paymentTerms}</span>}
              </div>
              {so.notes && <p className="mt-2 text-sm text-slate-600 italic bg-slate-50 px-3 py-1.5 rounded-lg">{so.notes}</p>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Challan view / creation commented out
              {(so.challanNumber || (so.linkedChallans && so.linkedChallans.length > 0)) ? (
                <button
                  onClick={() => {
                    const num = so.challanNumber || so.linkedChallans?.[0]?.challanNumber;
                    navigate('/sales/challans', { state: { searchChallanNo: num, openChallanNo: num } });
                  }}
                  className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 text-sm font-semibold hover:bg-emerald-100 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-truck-line text-sm text-emerald-600" /> View {so.challanNumber || so.linkedChallans?.[0]?.challanNumber}
                </button>
              ) : (
                itemsList.some((i) => i.pendingQty > 0) && (
                  <button
                    onClick={handleCreateChallan}
                    className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-indigo-200 text-indigo-600 text-sm font-medium hover:bg-indigo-50 cursor-pointer whitespace-nowrap transition-colors"
                  >
                    <i className="ri-truck-line text-sm" /> Create Challan
                  </button>
                )
              )}
              */}
              {itemsList.some((i) => i.pendingQty > 0) && (
                <button
                  onClick={handleCreateInvoice}
                  className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-file-text-line text-sm" /> Create Invoice
                </button>
              )}
              <button
                onClick={() => navigate('/sales/orders/new', { state: { editSO: so, soId: so.id } })}
                className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-edit-line text-sm" /> Edit
              </button>
              {canCancel && (
                <button
                  onClick={handleCancelOrder}
                  className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-red-200 text-red-600 text-sm font-medium hover:bg-red-50 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-close-circle-line text-sm" /> Cancel
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Two-column: Items + Timeline */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Items Table */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100">
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Order Items</h2>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {['Item', 'HSN', 'Unit', 'Ordered', 'Delivered', 'Pending', 'Rate', 'Total', 'Status'].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {itemsList.map((item) => {
                  const st = getItemStatus(item.deliveredQty, item.orderedQty);
                  return (
                    <tr key={item.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-slate-800">{item.itemName}</td>
                      <td className="px-4 py-3 text-slate-500">{item.hsnCode}</td>
                      <td className="px-4 py-3 text-slate-500">{item.unit}</td>
                      <td className="px-4 py-3 font-medium text-slate-700">{item.orderedQty}</td>
                      <td className="px-4 py-3 text-emerald-600 font-medium">{item.deliveredQty}</td>
                      <td className="px-4 py-3">
                        <span className={item.pendingQty > 0 ? 'text-amber-600 font-semibold' : 'text-slate-400'}>
                          {item.pendingQty}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatINR(item.rate)}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{formatINR(item.totalAmount)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${st.cls}`}>{st.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Right panel: Timeline + Summary */}
          <div className="space-y-4">
            {/* Order Summary */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Order Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Order Value</span>
                  <span className="font-semibold text-slate-800">{formatINR(so.totalAmount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Delivered</span>
                  <span className="font-semibold text-emerald-600">{formatINR(so.deliveredAmount)} ({pct}%)</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Pending</span>
                  <span className="font-semibold text-amber-600">{formatINR(so.pendingAmount)} ({100 - pct}%)</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
                  <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
            </div>

            {/* Fulfillment Timeline */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-4">Fulfillment Timeline</h2>
              <div className="space-y-3">
                {events.map((ev, i) => (
                  <div key={ev.id} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${ev.type === 'CREATED' ? 'bg-blue-400' : ev.type === 'CHALLAN' ? 'bg-amber-400' : 'bg-emerald-400'}`} />
                      {i < events.length - 1 && <div className="w-px flex-1 bg-slate-200 mt-0.5" />}
                    </div>
                    <div className="pb-3">
                      <p className="text-sm text-slate-700">{ev.description}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{ev.date}</p>
                      {/* View challan commented out
                      {(ev.type === 'CHALLAN' && ev.challanNumber) && (
                        <button onClick={() => navigate('/sales/challans')} className="text-xs text-indigo-500 hover:underline mt-0.5 cursor-pointer block">
                          View {ev.challanNumber}
                        </button>
                      )}
                      */}
                      {(ev.type === 'INVOICE' && ev.invoiceNumber) && (
                        <button onClick={() => navigate('/sales/invoices')} className="text-xs text-indigo-500 hover:underline mt-0.5 cursor-pointer block">
                          View {ev.invoiceNumber}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {events.length === 0 && <p className="text-sm text-slate-400">No events yet</p>}
              </div>
            </div>
          </div>
        </div>
      </div>

    </AppLayout>
  );
}